// buyer-fields.js (Dawn 15.4.1 SAFE)
// ─────────────────────────────────────────────────────────────────────────────
// - Montaje por instancia (data-bf-host) + idempotente (no duplica al hacer toggle)
// - Selectores scoped a cada bloque (data-bf-root + data-bf="...")
// - Mantiene tu lógica: intl-tel-input, validaciones, cart.attributes, LS por carrito,
//   title case, radios, recipient condicional, checkout guards, etc.
// ─────────────────────────────────────────────────────────────────────────────

(function () {
  // Utilidades globales
  const digitsOnly = (s) => String(s || '').replace(/\D/g, '');
  const isVisible = (el) => !!(el && el.offsetParent !== null);
  const libReady = () => typeof window.intlTelInput === 'function';

  const getITI = (input) => window.intlTelInputGlobals?.getInstance?.(input) || null;
  const getISO = (iti) => (iti?.getSelectedCountryData?.().iso2 || '').toUpperCase();
  const getDial = (iti) => {
    const d = iti?.getSelectedCountryData?.().dialCode;
    return d ? `+${String(d)}` : '';
  };
  const containerOf = (el) => el?.closest?.('.iti') || el;

  function stripIntlPrefix(input) {
    const raw = String(input?.value || '');
    if (/^\+\d/.test(raw)) input.value = raw.replace(/^\+\d+\s*/, '');
  }

  // Title Case (tu función original)
  function toTitleCaseName(s, locale = (document.documentElement.lang || 'es')) {
    if (!s) return '';
    s = String(s).normalize('NFC').trim().replace(/\s+/g, ' ');
    if (!s) return '';
    const keepLower = new Set(['de','del','la','las','los','y','e','da','das','do','dos','van','von','di',"de'","d'","l'","o'","du",'le','des']);
    const cap = (w) => w ? w[0].toLocaleUpperCase(locale) + w.slice(1) : w;
    const capSmart = (word, isEdge) => {
      const lower = word.toLocaleLowerCase(locale);
      if (!isEdge && keepLower.has(lower)) return lower;
      if (lower.includes("'")) return lower.split("'").map((seg,i)=> i? cap(seg): (keepLower.has(seg)&&!isEdge?seg:cap(seg))).join("'");
      if (lower.includes('-')) return lower.split('-').map(seg=>cap(seg)).join('-');
      if (/^mc[a-záéíóúüñ].*/.test(lower)) return 'Mc' + lower.slice(2).replace(/^./, c => c.toLocaleUpperCase(locale));
      return cap(lower);
    };
    const parts = s.split(' ');
    const lastIdx = parts.length - 1;
    return parts.map((w, i) => capSmart(w, (i === 0 || i === lastIdx))).join(' ');
  }

  function clearMsg(el) {
    if (!el) return;
    const c = containerOf(el);
    c?.classList?.remove('error');
    el.classList.remove('error');
    const next = c?.nextElementSibling;
    if (next?.classList?.contains('error-message')) next.remove();
    el.setCustomValidity?.('');
  }

  function showMsg(el, msg) {
    if (!el) return;
    const c = containerOf(el);
    c?.classList?.add('error');
    el.classList.add('error');
    const next = c?.nextElementSibling;
    if (next?.classList?.contains('error-message')) next.remove();
    const div = document.createElement('div');
    div.className = 'error-message';
    div.textContent = msg;
    c.insertAdjacentElement('afterend', div);
    el.setCustomValidity?.(msg);
  }

  function clearDeliveryError(root) {
    const ctr = root?.querySelector('.delivery-toggle__controls');
    const next = ctr?.nextElementSibling;
    if (next?.classList?.contains('error-message')) next.remove();
  }

  function destroyITI(input) {
    const prev = getITI(input);
    if (prev?.destroy) prev.destroy();
    const wrap = input.closest?.('.iti');
    if (wrap) {
      wrap.parentNode.insertBefore(input, wrap);
      wrap.remove();
    }
    input.classList.remove('iti__tel-input');
  }

  function initITI(input, savedISO) {
    if (!libReady() || !isVisible(input)) return null;
    if (input.closest('.iti')) destroyITI(input);

    return window.intlTelInput(input, {
      initialCountry: savedISO && /^[a-z]{2}$/i.test(savedISO) ? savedISO.toLowerCase() : 'auto',
      geoIpLookup: (cb) =>
        fetch('https://ipapi.co/json')
          .then((r) => r.json())
          .then((d) => cb(d.country_code))
          .catch(() => cb('ES')),
      separateDialCode: true,
      nationalMode: true,
      autoPlaceholder: 'polite',
      utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@25.5.2/build/js/utils.js'
    });
  }

  function e164From(iti, input) {
    if (!iti || !input) return '';
    const u = window.intlTelInputUtils;
    if (u && iti.getNumber) {
      const e = iti.getNumber(u.numberFormat.E164);
      if (e) return e;
    }
    const data = iti.getSelectedCountryData?.() || {};
    const dial = String(data.dialCode || '');
    let nat = digitsOnly(input.value);
    if (dial && nat.startsWith(dial)) nat = nat.slice(dial.length);
    return (dial ? `+${dial}${nat}` : `+${nat}`).replace(/\+\+/, '+');
  }

  function isValidPhone(iti, input) {
    if (!iti || !input) return false;
    const u = window.intlTelInputUtils;
    if (u && iti.isValidNumber && iti.getNumberType) {
      if (!iti.isValidNumber()) return false;
      const type = iti.getNumberType();
      const bad = new Set([u.numberType.TOLL_FREE,u.numberType.PREMIUM_RATE,u.numberType.SHARED_COST,u.numberType.PERSONAL_NUMBER]);
      if (bad.has(type)) return false;

      const iso = (iti.getSelectedCountryData?.().iso2 || '').toLowerCase();
      const nat = iti.getNumber(u.numberFormat.NATIONAL) || input.value;
      const d = digitsOnly(nat);

      if (iso === 'es') {
        if (d.length !== 9) return false;
        if (!/^[6789]/.test(d[0])) return false;
        if (new Set(['803','806','807','901','902']).has(d.slice(0,3))) return false;
      }
      return true;
    }
    return /^\+\d{8,15}$/.test(e164From(iti, input));
  }

  function samePhones(itiA, inputA, itiB, inputB) {
    const a = (e164From(itiA, inputA) || '').trim();
    const b = (e164From(itiB, inputB) || '').trim();
    return a && b && a === b;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Montaje por instancia (host)
  // ─────────────────────────────────────────────────────────────────────────────

  async function fetchCart() {
    try { return await fetch('/cart.js').then((r) => r.json()); }
    catch { return null; }
  }

  // === Cart update queue (anti-409) ===========================================
  const postCartUpdate = (() => {
    const URL = '/cart/update.js';
    const DEBOUNCE_MS = 700;

    let timer = null;
    let pending = null;           // último payload agregado
    let inFlight = Promise.resolve();

    const sleep = (ms) => new Promise(r => setTimeout(r, ms));

    async function send(attributes, attempt = 0) {
      try {
        const res = await fetch(URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
          cache: 'no-store',
          keepalive: true
        });

        // Shopify puede devolver 409 / 429 cuando hay concurrencia
        if ((res.status === 409 || res.status === 429) && attempt < 5) {
          const backoff = 200 * Math.pow(2, attempt) + Math.floor(Math.random() * 150);
          await sleep(backoff);
          return send(attributes, attempt + 1);
        }

        // Si no es ok, no rompas todo el flujo
        return res;
      } catch (_) {
        // fallo de red: reintento ligero
        if (attempt < 3) {
          await sleep(250 * (attempt + 1));
          return send(attributes, attempt + 1);
        }
      }
    }

    function schedule(attributes) {
      // Merge de parches: el último gana
      pending = { ...(pending || {}), ...(attributes || {}) };

      clearTimeout(timer);
      timer = setTimeout(() => {
        const payload = pending;
        pending = null;

        // Cola: garantiza 1 request a la vez
        inFlight = inFlight.then(() => send(payload));
      }, DEBOUNCE_MS);
    }

    schedule.flush = async () => {
      clearTimeout(timer);
      if (pending) {
        const payload = pending;
        pending = null;
        inFlight = inFlight.then(() => send(payload));
      }
      await inFlight;
    };

    return schedule;
  })();

  function mountOne(host) {
    if (!host || host.dataset.bfMounted === '1') return;
    host.dataset.bfMounted = '1';

    // Mover host (una sola vez) al footer
    const selector = host.getAttribute('data-move-into') || '#main-cart-footer .cart__footer';
    const target = document.querySelector(selector);
    if (target && !target.contains(host)) {
      target.insertBefore(host, target.firstElementChild || null);
    }

    const root = host.querySelector('[data-bf-root]');
    if (!root) return;

    // Selectores SCOPED (data-bf)
    const $ = (s) => root.querySelector(s);
    const $$ = (s) => Array.from(root.querySelectorAll(s));

    const buyerWrap     = $('[data-bf="buyer"]');
    const recipWrap     = $('[data-bf="recipient"]');
    const deliveryBlock = $('[data-bf="delivery"]');

    const buyerName   = $('[data-bf="buyer-name"]');
    const buyerPhone  = $('[data-bf="buyer-phone"]');
    const buyerE164   = $('[data-bf="buyer-phone-e164"]');

    const recipName   = $('[data-bf="recipient-name"]');
    const recipPhone  = $('[data-bf="recipient-phone"]');
    const recipE164   = $('[data-bf="recipient-phone-e164"]');

    const deliveryRadios = () => $$('[data-bf="deliver-to-other"]');
    const deliverySelected = () => deliveryRadios().some(r => r.checked);
    const isDeliverToOther = () => deliveryRadios().some(r => r.checked && r.value === 'yes');

    let INIT_DELIVERY = null;

    // Keys visibles en Shopify
    const DELIVERY_ATTR_KEY    = 'Entrega a otra persona';
    const BUYER_NAME_ATTR_KEY  = 'Nombre Comprador';
    const BUYER_PHONE_ATTR_KEY = 'Teléfono Comprador';
    const RECIP_NAME_ATTR_KEY  = 'Nombre Destinatario';
    const RECIP_PHONE_ATTR_KEY = 'Teléfono Destinatario';

    const texts = {
      buyerNameError:     buyerWrap?.dataset?.errBuyerName     || 'Please enter your name.',
      buyerPhoneError:    buyerWrap?.dataset?.errBuyerPhone    || 'Enter a valid phone number.',
      buyerSurnameError:  buyerWrap?.dataset?.errBuyerSurname  || 'Please also enter your surname.',
      rcptNameError:      recipWrap?.dataset?.errRcptName      || "Please enter recipient's name.",
      rcptPhoneError:     recipWrap?.dataset?.errRcptPhone     || 'Enter a valid recipient phone number.',
      rcptSurnameError:   recipWrap?.dataset?.errRcptSurname   || "Please also enter the recipient's surname.",
      deliveryError:      deliveryBlock?.dataset?.errDelivery  || 'Please select an option.',
      samePhoneError:     recipWrap?.dataset?.errSamePhone     || 'El teléfono del destinatario debe ser distinto al del comprador.'
    };

    const lang = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];

    // Visibilidad según carrito vacío (global)
    function setBuyerFieldsVisible(v) {
      if (!root) return;
      root.hidden = !v;
      root.style.display = v ? '' : 'none';
      root.setAttribute('aria-hidden', String(!v));
    }
    function refreshBuyerFieldsVisibility() {
      const footer = document.querySelector('#main-cart-footer');
      setBuyerFieldsVisible(!footer?.classList?.contains('is-empty'));
    }

    // LS versionada por carrito
    let CART_ID = 'no-cart';
    const LS_KEY = () => `bf:v2:${CART_ID}`;
    const readLS  = () => { try { return JSON.parse(localStorage.getItem(LS_KEY()) || '{}'); } catch { return {}; } };
    const writeLS = (obj) => localStorage.setItem(LS_KEY(), JSON.stringify(obj));

    // Sync State (tu función, igual)
    function syncState(partial) {
      const current = readLS() || {};
      const baseDelivery = (typeof current.delivery !== 'undefined')
        ? current.delivery
        : (INIT_DELIVERY ?? '');

      const next = { delivery: baseDelivery, buyer:{}, recipient:{}, ...current };

      const deep = (t, s) => {
        for (const k in s) {
          t[k] = (s[k] && typeof s[k]==='object' && !Array.isArray(s[k]))
            ? deep(t[k]||(t[k]={}), s[k])
            : s[k];
        }
        return t;
      };
      deep(next, partial);
      writeLS(next);

      const explicitDelivery =
        partial && Object.prototype.hasOwnProperty.call(partial, 'delivery');

      const patch = {
        [BUYER_NAME_ATTR_KEY]  : (next.buyer?.name  || '').trim(),
        [BUYER_PHONE_ATTR_KEY] : (next.buyer?.e164 || '').trim(),
        'Idioma Comprador'     : lang
      };

      const dval = (next.delivery || '').toLowerCase();
      if (explicitDelivery || dval === 'yes' || dval === 'no') {
        patch[DELIVERY_ATTR_KEY] = dval;
      }

      if (dval === 'yes') {
        patch[RECIP_NAME_ATTR_KEY]  = (next.recipient?.name  || '').trim();
        patch[RECIP_PHONE_ATTR_KEY] = (next.recipient?.e164 || '').trim();
      } else if (explicitDelivery) {
        patch[RECIP_NAME_ATTR_KEY]  = '';
        patch[RECIP_PHONE_ATTR_KEY] = '';
      }

      postCartUpdate(patch);
    }

    // ITI instances
    let itiBuyer=null, itiRecip=null;

    function bindPhoneEvents(role, inst, input, hidden){
      if (!input || input.dataset.bound === '1') return;

      const onAny = () => {
        clearMsg(input);
        stripIntlPrefix(input);

        const e164 = inst ? e164From(inst, input) : (hidden?.value || '');
        const iso  = inst ? getISO(inst) : '';
        const dial = inst ? getDial(inst) : '';

        if (hidden) hidden.value = e164;

        if (role === 'buyer') {
          syncState({ buyer: { e164, iso, dial } });
        } else {
          if (!isDeliverToOther()) return;
          validatePhonesLive();
          syncState({ recipient: { e164, iso, dial } });
        }
      };

      ['input','countrychange','blur','change'].forEach(ev => input.addEventListener(ev, onAny));
      input.dataset.bound = '1';
    }

    function savedFor(role){
      try{
        const st = readLS() || {};
        const node = role === 'buyer' ? (st.buyer || {}) : (st.recipient || {});
        return { savedE164: node.e164 || '', savedISO: node.iso || '' };
      }catch(_){ return { savedE164:'', savedISO:'' }; }
    }

    function setupPhoneField(role, { savedE164='', savedISO='' } = {}){
      const input  = role==='buyer' ? buyerPhone : recipPhone;
      const hidden = role==='buyer' ? buyerE164  : recipE164;
      if (!input) return null;

      if (!libReady() || !isVisible(input)) {
        setTimeout(()=>setupPhoneField(role, { savedE164, savedISO }), 80);
        return null;
      }

      if (savedE164 && !input.value) input.value = savedE164;

      const inst = initITI(input, (savedISO||'').toLowerCase());
      const awaited = (inst && inst.promise) ? inst.promise : Promise.resolve();

      awaited.then(()=>{
        try { if (input.value && inst?.setNumber) inst.setNumber(input.value); } catch(_) {}

        const e164 = inst ? e164From(inst, input) : (hidden?.value || '');
        const iso  = inst ? getISO(inst) : '';
        const dial = inst ? getDial(inst) : '';

        if (hidden) hidden.value = e164;
        input.classList.add('js-iti-ready');
        bindPhoneEvents(role, inst || null, input, hidden);

        if (role==='buyer') syncState({ buyer: { e164, iso, dial } });
        else if (isDeliverToOther()) syncState({ recipient: { e164, iso, dial } });
      });

      if (role==='buyer') itiBuyer = inst; else itiRecip = inst;
      return inst;
    }

    function validatePhonesLive(){
      if (!isDeliverToOther() || !buyerPhone || !recipPhone) return;
      const instB = itiBuyer || getITI(buyerPhone);
      const instR = itiRecip || getITI(recipPhone);
      if (!instB || !instR) return;
      const hasAny = digitsOnly(recipPhone.value).length > 0;
      clearMsg(recipPhone);
      if (!hasAny) return;
      if (samePhones(instB,buyerPhone,instR,recipPhone)) showMsg(recipPhone, texts.samePhoneError);
    }

    function toggleRecipient(){
      const show = isDeliverToOther();
      if (recipWrap) {
        recipWrap.classList.toggle('is-hidden', !show);
        recipWrap.setAttribute('aria-hidden', String(!show));
        recipWrap.querySelectorAll('[cond-required="true"]').forEach(el=>{
          if (show) el.setAttribute('required','required');
          else { el.removeAttribute('required'); clearMsg(el); }
        });
      }
      clearDeliveryError(root);
      if (show) setupPhoneField('recipient', savedFor('recipient'));
      setTimeout(validatePhonesLive, 0);
    }

    function bindNameField(el, role) {
      if (!el || el.dataset.boundName === '1') return;
      ['input','change','blur'].forEach(ev => {
        el.addEventListener(ev, (e) => {
          clearMsg(el);
          let val = el.value;
          if (e.type !== 'input') {
            const norm = toTitleCaseName(val);
            if (norm !== val) { el.value = norm; val = norm; }
          } else {
            val = val.trim();
          }
          if (role === 'recipient' && !isDeliverToOther()) return;
          const patch = {}; patch[role] = { name: val };
          syncState(patch);
        });
      });
      el.dataset.boundName = '1';
    }

    async function validateBeforeLeave(){
      [buyerName,buyerPhone,recipName,recipPhone].forEach(clearMsg);
      root.querySelectorAll('.error-message').forEach(n=>{
        if (!n.previousElementSibling?.classList?.contains('delivery-toggle__controls')) n.remove();
      });

      if (buyerName) buyerName.value = toTitleCaseName(buyerName.value);
      if (recipName) recipName.value = toTitleCaseName(recipName.value);

      let ok = true;

      if (!buyerName?.value.trim()){ showMsg(buyerName, texts.buyerNameError); ok=false; }
      else if (buyerName.value.trim().split(/\s+/).length<2){ showMsg(buyerName, texts.buyerSurnameError); ok=false; }

      const bIti = itiBuyer || getITI(buyerPhone);
      if (buyerE164 && bIti) buyerE164.value = e164From(bIti, buyerPhone);
      if (!isValidPhone(bIti, buyerPhone)){ showMsg(buyerPhone, texts.buyerPhoneError); ok=false; }

      if (!deliverySelected()){
        const ctr = root.querySelector('.delivery-toggle__controls');
        if (ctr && !ctr.nextElementSibling?.classList?.contains('error-message')){
          const div = document.createElement('div');
          div.className='error-message';
          div.textContent = texts.deliveryError;
          ctr.insertAdjacentElement('afterend', div);
        }
        ok=false;
      }

      if (isDeliverToOther()){
        const rIti = itiRecip || getITI(recipPhone);
        if (recipE164 && rIti) recipE164.value = e164From(rIti, recipPhone);

        if (!recipName?.value.trim()){ showMsg(recipName, texts.rcptNameError); ok=false; }
        else if (recipName.value.trim().split(/\s+/).length<2){ showMsg(recipName, texts.rcptSurnameError); ok=false; }

        const dup = samePhones(bIti,buyerPhone,rIti,recipPhone);
        if (dup){ showMsg(recipPhone, texts.samePhoneError); ok=false; }
        else if (!isValidPhone(rIti,recipPhone)){ showMsg(recipPhone, texts.rcptPhoneError); ok=false; }
      }

      syncState({
        delivery: isDeliverToOther() ? 'yes' : (deliverySelected() ? 'no' : ''),
        buyer: { e164: (buyerE164?.value || '').trim(), name: (buyerName?.value || '').trim() },
        recipient: isDeliverToOther()
          ? { e164: (recipE164?.value || '').trim(), name: (recipName?.value || '').trim() }
          : {}
      });

      try { await postCartUpdate.flush(); } catch(_) {}
      return ok;
    }

    function guardCheckout(){
      const form =
        document.querySelector('#CartDrawer-Form') ||
        document.querySelector('form#cart[action="/cart"]') ||
        document.querySelector('form[action="/cart"]') ||
        document.querySelector('form[action^="/cart"]');

      if (form && !form._bf_guarded){
        form.addEventListener('submit', async (e)=>{
          const ok = await validateBeforeLeave();
          if (!ok){ e.preventDefault(); e.stopPropagation(); return; }
        }, true);
        form._bf_guarded = true;
      }

      const btnSel = [
        '[name="checkout"]',
        '#CartDrawer-Checkout',
        '.cart__ctas [type="submit"]',
        'button[data-test="checkout-button"]',
        'a[href="/checkout"]'
      ].join(',');

      document.querySelectorAll(btnSel).forEach(btn=>{
        if (btn._bf_guarded) return;
        btn.addEventListener('click', async (e)=>{
          const ok = await validateBeforeLeave();
          if (!ok){ e.preventDefault(); e.stopImmediatePropagation(); return; }
        }, true);
        btn._bf_guarded = true;
      });
    }

    // INIT
    (async function init(){
      // Solo hidden via attributes
      buyerPhone?.removeAttribute('name');
      recipPhone?.removeAttribute('name');

      const cart = await fetchCart();
      CART_ID = (cart && (cart.token || cart.id || cart.cart_token)) ? (cart.token || cart.id || cart.cart_token) : 'no-cart';

      const st = readLS();
      const attrs = cart?.attributes || {};

      // Restaurar radios (scope)
      const savedDelivery = (st.delivery || attrs[DELIVERY_ATTR_KEY] || '').toLowerCase();
      const radios = deliveryRadios();
      radios.forEach(r => {
        r.checked = (savedDelivery && r.value === savedDelivery) ? true : r.checked;
      });
      INIT_DELIVERY = (savedDelivery === 'yes') ? 'yes' : (savedDelivery === 'no') ? 'no' : '';

      // Restaurar nombres
      if (buyerName && !buyerName.value) buyerName.value = st.buyer?.name || attrs[BUYER_NAME_ATTR_KEY] || '';
      if (recipName && !recipName.value) recipName.value = st.recipient?.name || attrs[RECIP_NAME_ATTR_KEY] || '';
      if (buyerName) buyerName.value = toTitleCaseName(buyerName.value);
      if (recipName) recipName.value = toTitleCaseName(recipName.value);

      bindNameField(buyerName, 'buyer');
      bindNameField(recipName, 'recipient');

      toggleRecipient();

      setupPhoneField('buyer', {
        savedE164: st.buyer?.e164 || (attrs[BUYER_PHONE_ATTR_KEY]||''),
        savedISO:  st.buyer?.iso  || ''
      });

      if (isDeliverToOther()){
        setupPhoneField('recipient', {
          savedE164: st.recipient?.e164 || (attrs[RECIP_PHONE_ATTR_KEY]||''),
          savedISO:  st.recipient?.iso  || ''
        });
      }

      // Cambio radios
      deliveryRadios().forEach(r => r.addEventListener('change', async () => {
        clearDeliveryError(root);
        toggleRecipient();
        syncState({ delivery: isDeliverToOther() ? 'yes' : 'no' });
        try { await postCartUpdate.flush(); } catch(_) {}
      }));

      refreshBuyerFieldsVisibility();
      guardCheckout();

      // Primera sync
      syncState({
        delivery: isDeliverToOther() ? 'yes' : (deliverySelected() ? 'no' : ''),
        buyer: {
          name: (buyerName?.value || '').trim(),
          e164: (buyerE164?.value || '').trim()
        },
        recipient: isDeliverToOther()
          ? { name: (recipName?.value || '').trim(), e164: (recipE164?.value || '').trim() }
          : {}
      });

    })();

    // Theme editor re-renders: revalidar recipient si procede
    document.addEventListener('shopify:section:load', () => {
      if (isDeliverToOther()) setupPhoneField('recipient', savedFor('recipient'));
      guardCheckout();
    });

    // Flush al salir
    window.addEventListener('pagehide', () => { try { postCartUpdate.flush(); } catch(_) {} });
    window.addEventListener('beforeunload', () => { try { postCartUpdate.flush(); } catch(_) {} });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') { try { postCartUpdate.flush(); } catch(_) {} }
    });
  }

  function mountAll() {
    document.querySelectorAll('[data-bf-host]').forEach(mountOne);
  }

  document.addEventListener('DOMContentLoaded', mountAll);
  document.addEventListener('shopify:section:load', mountAll);
  document.addEventListener('shopify:section:select', mountAll);

})();
